import { useMemo } from 'react';
import './AgentOrchestration.css';

interface AgentNode {
  id: string;
  label: string;
  /** Position in % of the viewBox (0-100). */
  x: number;
  y: number;
  /** Stagger the "thinking" indicator so agents feel alive, not synced. */
  delay: number;
}

interface AgentOrchestrationProps {
  /** Optional heading rendered above the visual. */
  title?: string;
  /** Optional sub-label rendered under the title. */
  subtitle?: string;
  /** Labels for the surrounding agents. Defaults to four generic agents. */
  agents?: string[];
  /** Label for the central coordinator node. */
  hubLabel?: string;
  className?: string;
}

const DEFAULT_AGENTS = ['Research', 'Analyze', 'Generate', 'Review'];

/**
 * A minimalist, self-contained animation of several AI agents collaborating
 * around a central orchestrator. Pure SVG + CSS (no canvas, no deps), so it
 * stays crisp at any size and respects reduced-motion preferences.
 */
export function AgentOrchestration({
  title = 'Multi-Agent Orchestration',
  subtitle = 'Agents coordinate in real time to complete the task',
  agents = DEFAULT_AGENTS,
  hubLabel = 'Orchestrator',
  className,
}: AgentOrchestrationProps) {
  const nodes = useMemo<AgentNode[]>(() => {
    const cx = 50;
    const cy = 50;
    const radius = 34;
    const count = agents.length;
    return agents.map((label, i) => {
      // Distribute evenly around the hub, starting from the top.
      const angle = (-90 + (360 / count) * i) * (Math.PI / 180);
      return {
        id: `agent-${i}`,
        label,
        x: cx + radius * Math.cos(angle),
        y: cy + radius * Math.sin(angle),
        delay: i * 0.6,
      };
    });
  }, [agents]);

  return (
    <div className={`agent-orch${className ? ` ${className}` : ''}`}>
      <svg
        className="agent-orch__svg"
        viewBox="0 0 100 100"
        role="img"
        aria-label="Multiple AI agents collaborating around a central orchestrator"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <radialGradient id="agentOrchHub" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#4338ca" />
          </radialGradient>
          <linearGradient id="agentOrchLink" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#4f46e5" stopOpacity="0.05" />
            <stop offset="50%" stopColor="#6366f1" stopOpacity="0.45" />
            <stop offset="100%" stopColor="#4f46e5" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* Connection lines + traveling data pulses (drawn first, behind nodes) */}
        {nodes.map((n, i) => (
          <g key={`link-${n.id}`}>
            <line
              className="agent-orch__link"
              x1="50"
              y1="50"
              x2={n.x}
              y2={n.y}
            />
            {/* Pulse traveling hub -> agent */}
            <circle
              className="agent-orch__pulse"
              r="1.1"
              style={{ animationDelay: `${i * 0.5}s` }}
            >
              <animateMotion
                dur="2.6s"
                repeatCount="indefinite"
                begin={`${i * 0.5}s`}
                path={`M50,50 L${n.x},${n.y}`}
              />
            </circle>
            {/* Pulse traveling agent -> hub (the "result" coming back) */}
            <circle
              className="agent-orch__pulse agent-orch__pulse--return"
              r="0.9"
              style={{ animationDelay: `${i * 0.5 + 1.3}s` }}
            >
              <animateMotion
                dur="2.6s"
                repeatCount="indefinite"
                begin={`${i * 0.5 + 1.3}s`}
                path={`M${n.x},${n.y} L50,50`}
              />
            </circle>
          </g>
        ))}

        {/* Surrounding agent nodes */}
        {nodes.map((n) => (
          <g
            key={n.id}
            className="agent-orch__agent"
            style={{ animationDelay: `${n.delay}s` }}
          >
            <circle
              className="agent-orch__agent-ring"
              cx={n.x}
              cy={n.y}
              r="7.5"
              style={{ animationDelay: `${n.delay}s` }}
            />
            <circle
              className="agent-orch__agent-dot"
              cx={n.x}
              cy={n.y}
              r="5"
            />
            {/* Three "thinking" dots inside each agent */}
            <g className="agent-orch__think" style={{ animationDelay: `${n.delay}s` }}>
              <circle cx={n.x - 1.8} cy={n.y} r="0.6" style={{ animationDelay: `${n.delay}s` }} />
              <circle cx={n.x} cy={n.y} r="0.6" style={{ animationDelay: `${n.delay + 0.2}s` }} />
              <circle cx={n.x + 1.8} cy={n.y} r="0.6" style={{ animationDelay: `${n.delay + 0.4}s` }} />
            </g>
          </g>
        ))}

        {/* Central orchestrator hub */}
        <g className="agent-orch__hub">
          <circle className="agent-orch__hub-halo" cx="50" cy="50" r="13" />
          <circle className="agent-orch__hub-core" cx="50" cy="50" r="9" />
          {/* Brain pattern */}
          <g className="agent-orch__brain" transform="translate(50 49.5) scale(0.072)" aria-hidden>
            <path
              d="M-6 -36c-9 0 -16 7 -16 16c0 1 0 2 .3 3c-6 2 -10 8 -10 15c0 5 2 9 6 12
                 c-1 2 -2 5 -2 8c0 9 7 16 16 16c4 0 8 -2 11 -5l.4 .4V-39c-2 -1.9 -5 -3 -8 -3z"
            />
            <path
              d="M6 -36c9 0 16 7 16 16c0 1 0 2 -.3 3c6 2 10 8 10 15c0 5 -2 9 -6 12
                 c1 2 2 5 2 8c0 9 -7 16 -16 16c-4 0 -8 -2 -11 -5l-.4 .4V-39c2 -1.9 5 -3 8 -3z"
            />
            <path
              className="agent-orch__brain-lines"
              d="M-3 -24c-5 1 -7 5 -7 9M-2 -6c-6 0 -9 3 -9 8M3 -24c5 1 7 5 7 9M2 -6c6 0 9 3 9 8M0 -39V36"
              fill="none"
            />
          </g>
          <circle className="agent-orch__hub-spark" cx="50" cy="50" r="2" />
        </g>
      </svg>

      <div className="agent-orch__labels" aria-hidden>
        {nodes.map((n) => (
          <span
            key={`label-${n.id}`}
            className="agent-orch__label"
            style={{ left: `${n.x}%`, top: `${n.y + 9.5}%` }}
          >
            {n.label}
          </span>
        ))}
        <span className="agent-orch__label agent-orch__label--hub" style={{ left: '50%', top: '66%' }}>
          {hubLabel}
        </span>
      </div>

      {(title || subtitle) && (
        <div className="agent-orch__caption">
          {title && <h3 className="agent-orch__title">{title}</h3>}
          {subtitle && <p className="agent-orch__subtitle">{subtitle}</p>}
        </div>
      )}
    </div>
  );
}
